// The only code in the dashboard that calls fetch. Every API call goes through apiRequest: same-origin /api paths,
// JSON in and out, the session cookie sent by the browser, and errors turned into ApiError with the platform's
// stable errorCode so screens can branch on it.
//
//   const { user } = await apiRequest<{ user: User }>('POST', '/api/auth/login', { email, password })

/** Error codes from the platform (apps/platform/src/common/errors.ts), plus NETWORK when it can't be reached. */
export type ErrorCode =
  | 'INVALID_INPUT'
  | 'UNAUTHENTICATED'
  | 'INVALID_CREDENTIALS'
  | 'EMAIL_NOT_VERIFIED'
  | 'EMAIL_TAKEN'
  | 'SIGNUP_CLOSED'
  | 'TOKEN_INVALID'
  | 'FORBIDDEN'
  | 'ORG_SUSPENDED'
  | 'NOT_FOUND'
  | 'CONFLICT'
  | 'BAD_ORIGIN'
  | 'PAYLOAD_TOO_LARGE'
  | 'RATE_LIMITED'
  | 'NOT_CONFIGURED'
  | 'CALL_ENDED'
  | 'NUMBER_NOT_ALLOWED'
  | 'LINES_BUSY'
  | 'AGENT_UNAVAILABLE'
  | 'PHONE_UNAVAILABLE'
  | 'ACTION_NOT_ALLOWED'
  | 'ACTIONS_NOT_LIVE'
  | 'FLOW_INVALID'
  | 'NOT_TESTED'
  | 'INTERNAL'
  | 'NETWORK'

export class ApiError extends Error {
  readonly code: ErrorCode
  readonly status: number

  constructor(code: ErrorCode, message: string, status: number) {
    super(message)
    this.name = 'ApiError'
    this.code = code
    this.status = status
  }
}

const REQUEST_TIMEOUT_MS = 15_000
/** A voice preview may be rendered for the first time. */
const BLOB_TIMEOUT_MS = 30_000

type Method = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'

export const apiRequest = async <Data>(method: Method, path: string, body?: unknown): Promise<Data> => {
  let response: Response
  try {
    response = await fetch(path, {
      method,
      credentials: 'same-origin',
      headers: body === undefined ? undefined : { 'content-type': 'application/json' },
      body: body === undefined ? undefined : JSON.stringify(body),
      signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS),
    })
  } catch {
    throw new ApiError('NETWORK', 'Can’t reach RowCall. Check your connection and try again.', 0)
  }
  let json: ({ ok: true } & Data) | { ok: false; error: string; errorCode: ErrorCode }
  try {
    json = (await response.json()) as typeof json
  } catch {
    throw new ApiError('INTERNAL', 'RowCall sent an unexpected answer. Try again.', response.status)
  }
  if (!json.ok) throw new ApiError(json.errorCode, json.error, response.status)
  return json
}

/** A POST that answers with a file (a voice preview): the bytes as a Blob, or the platform's error as ApiError. */
export const apiBlob = async (path: string, body: unknown): Promise<Blob> => {
  let response: Response
  try {
    response = await fetch(path, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
      signal: AbortSignal.timeout(BLOB_TIMEOUT_MS),
    })
  } catch {
    throw new ApiError('NETWORK', 'Can’t reach RowCall. Check your connection and try again.', 0)
  }
  if (response.ok) return response.blob()
  const failure = await _failureOf(response)
  throw new ApiError(failure.errorCode ?? 'INTERNAL', failure.error ?? 'That didn’t work. Try again.', response.status)
}

/** The platform's error body, or nothing when the answer wasn't JSON. */
const _failureOf = async (response: Response): Promise<{ error?: string; errorCode?: ErrorCode }> => {
  try {
    return (await response.json()) as { error?: string; errorCode?: ErrorCode }
  } catch {
    return {}
  }
}

/** Whether an error means the person is signed out (the session ended or never existed). */
export const isSignedOut = (error: unknown): boolean => error instanceof ApiError && error.code === 'UNAUTHENTICATED'

/** A sentence to show for any error. */
export const errorMessage = (error: unknown): string =>
  error instanceof ApiError ? error.message : 'Something went wrong. Try again.'
